import { useTranslation } from "react-i18next";

import type { Hint } from "@/lib/hints";

// Live-Hinweise zur Konsistenz der Konstruktion („Dialogisches Alignment").
// Gruppiert nach Severity wie das FeasibilityPanel: Misalignment blockiert
// zwar nicht, ist aber der häufigste Audit-Abzug; Spannungen sind Warnungen.
// Fix-Buttons deeplinken in den Step, in dem sich der Hinweis beheben lässt.
export default function HintsPanel<K extends string | number>({
  hints,
  iloLabel,
  tlaLabel,
  atLabel,
  onGoto,
}: {
  hints: Hint<K>[];
  iloLabel: (key: K) => string;
  tlaLabel: (key: K) => string;
  atLabel: (key: K) => string;
  onGoto: (step: number) => void;
}) {
  const { t } = useTranslation();

  const values = (h: Hint<K>): Record<string, string | number> => ({
    ...(h.iloKey !== undefined ? { ilo: iloLabel(h.iloKey) } : {}),
    ...(h.tlaKey !== undefined ? { tla: tlaLabel(h.tlaKey) } : {}),
    ...(h.atKey !== undefined ? { at: atLabel(h.atKey) } : {}),
    ...(h.distance != null ? { distance: h.distance } : {}),
  });

  if (hints.length === 0) {
    return (
      <div className="p-4 bg-ca-card rounded-2xl border border-ca-surface-border">
        <p className="text-xs font-semibold text-verdict-green">
          ✓ {t("hints.allClear")}
        </p>
      </div>
    );
  }

  const misalignments = hints.filter((h) => h.severity === "misalignment");
  const tensions = hints.filter((h) => h.severity === "tension");

  return (
    <div className="space-y-4">
      {misalignments.length > 0 && (
        <div className="p-6 bg-ca-card rounded-2xl border-2 border-verdict-red/60">
          <h3 className="text-lg font-black text-verdict-red uppercase tracking-tight mb-1">
            {t("hints.misalignmentTitle")}
          </h3>
          <p className="text-sm text-ca-surface-muted mb-4">
            {t("hints.misalignmentBody")}
          </p>
          <ul className="space-y-3">
            {misalignments.map((h, i) => (
              <li key={i} className="flex flex-wrap items-center gap-3">
                <span className="flex-1 min-w-60 text-sm text-ca-surface-ink">
                  {t(`hints.rules.${h.code}`, values(h))}
                </span>
                <button
                  onClick={() => onGoto(h.step)}
                  className="px-3 py-1.5 rounded-md bg-ca-accent text-ca-brand text-xs font-bold hover:bg-brand-gold-dark transition"
                >
                  {t("hints.fixNow")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tensions.length > 0 && (
        <div className="p-5 bg-ca-card rounded-2xl border border-ca-surface-border">
          <h4 className="text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-3">
            {t("hints.tensionTitle")}
          </h4>
          <ul className="space-y-2">
            {tensions.map((h, i) => (
              <li key={i} className="flex flex-wrap items-center gap-3">
                <span className="flex-1 min-w-60 text-xs text-ca-surface-muted">
                  {t(`hints.rules.${h.code}`, values(h))}
                </span>
                <button
                  onClick={() => onGoto(h.step)}
                  className="text-xs font-semibold text-ca-surface-muted hover:text-ca-surface-accent transition"
                >
                  {t("hints.fix")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
